import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import "./RichTextEditor.css";

// La barre d'outils ne produit plus que les balises listées ci-dessous.
// `blockquote`/`hr`/`span`/`style` ne sont plus accessibles depuis l'éditeur
// mais sont conservés pour l'affichage de contenu déjà en base.
// DOMPurify filtre les valeurs CSS dangereuses (url(), expression…).
//
// div/dl/dt/dd/h1/h4/h5/h6 : jamais produits par l'éditeur, mais présents
// dans du contenu ancien importé de l'ex-site France Terme. Sans eux, DOMPurify
// retire la balise en ne gardant que le texte (comportement par défaut), ce
// qui colle tout le contenu sans retour à la ligne à l'affichage (ex. Note
// d'une fiche issue de l'import). Les garder dans la liste blanche permet un
// affichage lisible via leur mise en bloc par défaut du navigateur, sans
// risque de sécurité supplémentaire (scripts/attributs dangereux toujours filtrés).
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "sub", "sup", "a", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "hr", "span", "div", "dl", "dt", "dd"],
  ALLOWED_ATTR: ["href", "target", "rel", "style"],
};

// Exporté pour que l'affichage en lecture (ex. ModalFiche.jsx) applique
// exactement la même liste blanche que la saisie, plutôt qu'une sanitisation
// dupliquée et potentiellement désynchronisée.
export function sanitize(html) {
  return DOMPurify.sanitize(html || "", SANITIZE_CONFIG);
}

function ToolbarButton({ onClick, active, disabled, label, children }) {
  return (
    <button
      type="button"
      // onMouseDown + preventDefault : évite que le bouton ne vole le focus
      // de l'éditeur avant que la commande (ex. toggleBold) ne s'applique.
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      disabled={disabled}
      className={`rte-toolbar-btn${active ? " rte-toolbar-btn--active" : ""}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

/**
 * Éditeur WYSIWYG pour les champs texte riche d'une fiche (Définition,
 * Attention, Note, Commentaire) : mise en forme (gras/italique/souligné/
 * barré/exposant/indice), titres, listes, lien, annuler/rétablir, effacer
 * la mise en forme.
 *
 * S'intègre comme un <textarea> contrôlé : `onChange` reçoit un événement
 * synthétique {target: {name, value}}, compatible avec le handleChange
 * générique déjà utilisé par le formulaire. `value` est du HTML sanitizé ;
 * un texte brut existant (saisi avant le passage au WYSIWYG) s'affiche tel
 * quel, comme un simple paragraphe.
 */
export default function RichTextEditor({ name, value, onChange, minHeight = 80, style, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true },
      }),
      Subscript,
      Superscript,
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? "" : sanitize(editor.getHTML());
      onChange({ target: { name, value: html, type: "text" } });
    },
  });

  // Resynchronise l'éditeur quand `value` change depuis l'extérieur (ex.
  // chargement d'une fiche existante, arrivé après le montage initial),
  // sans écraser la frappe en cours.
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(incoming, false);
    }
  }, [editor, value]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien :", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const blockType = editor.isActive("heading", { level: 2 })
      ? "h2"
      : editor.isActive("heading", { level: 3 })
      ? "h3"
      : "paragraph";

  const setBlockType = (type) => {
    const chain = editor.chain().focus();
    if (type === "h2") chain.setHeading({ level: 2 }).run();
    else if (type === "h3") chain.setHeading({ level: 3 }).run();
    else chain.setParagraph().run();
  };

  return (
    <div className="rte-wrapper" style={style}>
      <div className="rte-toolbar">
        <ToolbarButton label="Annuler" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          ↶
        </ToolbarButton>
        <ToolbarButton label="Rétablir" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          ↷
        </ToolbarButton>

        <span className="rte-toolbar-sep" />
        <ToolbarButton label="Gras" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <span style={{ fontWeight: 700 }}>G</span>
        </ToolbarButton>
        <ToolbarButton label="Italique" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <span style={{ fontStyle: "italic" }}>I</span>
        </ToolbarButton>
        <ToolbarButton label="Souligné" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <span style={{ textDecoration: "underline" }}>S</span>
        </ToolbarButton>
        <ToolbarButton label="Barré" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <span style={{ textDecoration: "line-through" }}>B</span>
        </ToolbarButton>
        <ToolbarButton label="Indice" active={editor.isActive("subscript")} onClick={() => editor.chain().focus().toggleSubscript().run()}>
          X₂
        </ToolbarButton>
        <ToolbarButton label="Exposant" active={editor.isActive("superscript")} onClick={() => editor.chain().focus().toggleSuperscript().run()}>
          X²
        </ToolbarButton>

        <span className="rte-toolbar-sep" />
        <ToolbarButton label="Liste à puces" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          ≡
        </ToolbarButton>
        <ToolbarButton label="Liste numérotée" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1.
        </ToolbarButton>

        <span className="rte-toolbar-sep" />
        <ToolbarButton label="Lien" active={editor.isActive("link")} onClick={setLink}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 15l6 -6" />
            <path d="M11 6l.463 -.536a5 5 0 0 1 7.071 7.072l-.534 .464" />
            <path d="M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.534 -.463" />
          </svg>
        </ToolbarButton>

        <span className="rte-toolbar-sep" />
        <ToolbarButton label="Effacer la mise en forme" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
          Ø
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} className="rte-content" style={{ minHeight }} />
    </div>
  );
}
